import type Distribution from '@chilipiper/api-client/src/distribution'
import {
  AssignmentType,
  AssignmentTypeConfig,
  CallerRef,
  DistributedAssignmentLimit,
  DraftWrappedConfigurationDTO,
  UpdateDistributionRequest,
  WrappedConfigurationDTO,
  ProductFeatureWithRoundRobin,
  UsersOnVacationHandling,
} from '@chilipiper/api-client/src/distribution'
import * as R from 'remeda'
import { useDistributionWithExtraDataLoader, usePublishedDistributionLoader } from '../loaders'
import {
  checkIfHasNewDraftDTO,
  checkIfHasNewDraftModel,
  getReassignmentType,
  getRoundRobinKind,
} from './helpers'
import { DistributionData } from './queries'

const mapCallerRefToUserId = (user: CallerRef | string | undefined): string | undefined => {
  if (!user) {
    return undefined
  }

  return typeof user === 'string' ? user.split('/')[1] : user.id
}

type DistributionDraftInput = {
  distributionId: string
  workspaceId: string
  assignmentTypeConfig: AssignmentTypeConfig
  name?: string
  teamRef?: { id: string }
  weights?: ReadonlyArray<{ userId: string; weight: number }>
  manualCalibration?: ReadonlyArray<{ userId: string; calibration: number }>
  capping?: DistributedAssignmentLimit
  metadata: {
    updatedAt: string
    updatedBy: CallerRef
    revision: number
  }
}
type DistributionPublishedInput = {
  distributionId: string
  //TODO how published distribution can miss workspaceId?? Maxim marked this prop as "to be removed" on BE and as optional, we should ask him about that (currently on paternity leave)
  workspaceId?: string
  assignmentTypeConfig: AssignmentTypeConfig
  name?: string
  teamRef: { id: string }
  weights?: ReadonlyArray<{ userId: string; weight: number }>
  manualCalibration?: ReadonlyArray<{ userId: string; calibration: number }>
  capping?: DistributedAssignmentLimit
  metadata: {
    updatedAt: string
    updatedBy: CallerRef
    revision: number
    createdAt: string
  }
  deleteMetadata?: {
    deletedAt: string
    deletedBy: CallerRef
  }
}

const getDistributionData = (data: {
  draft?: DistributionDraftInput
  published?: DistributionPublishedInput
}): DistributionData => {
  const { draft, published } = data

  const parsedDraft = draft || published

  if (!parsedDraft) {
    throw new Error('No draft or published data')
  }

  const status = (() => {
    if (!published) {
      return 'draft'
    }

    if (!draft) {
      return 'published'
    }

    return checkIfHasNewDraftDTO({
      draft,
      published,
    })
      ? 'pending-changes'
      : 'published'
  })()

  const handlingConfig = (() => {
    if (parsedDraft.assignmentTypeConfig.type !== 'Meeting') {
      return {}
    }

    if (parsedDraft.assignmentTypeConfig.handling.type === 'Flexible') {
      return {
        rescheduleWithAnyTeamMember:
          parsedDraft.assignmentTypeConfig.handling.reassignmentType === 'AnyTeamMember',
        allowPickingAssignee: parsedDraft.assignmentTypeConfig.handling.allowPickingAssignee,
      }
    }

    return {
      hideAssignee: parsedDraft.assignmentTypeConfig.handling.hideAssignee,
    }
  })()

  const meetingLimit = (() => {
    if (parsedDraft.assignmentTypeConfig.type !== 'Meeting') {
      return undefined
    }

    if (parsedDraft.assignmentTypeConfig.limits.type !== 'MeetingLimitSet') {
      return undefined
    }

    return {
      userCaps: parsedDraft.assignmentTypeConfig.limits.userCaps ?? [],
      resetPeriodicity: parsedDraft.assignmentTypeConfig.limits.resetPeriodicity,
    }
  })()

  return {
    id: parsedDraft.distributionId,
    currentData: {
      assignmentType: parsedDraft.assignmentTypeConfig.type,
      roundRobinKind:
        parsedDraft.assignmentTypeConfig.type === 'Meeting'
          ? parsedDraft.assignmentTypeConfig.handling.type
          : 'Strict',
      handlingConfig,
      name: parsedDraft.name,
      publishedName: published?.name,
      status,
      lastModifiedAt: parsedDraft.metadata.updatedAt,
      lastModifiedBy: parsedDraft.metadata.updatedBy,
      lastPublishedAt: published?.metadata.updatedAt,
      firstPublishedAt: published?.metadata.createdAt,
      lastPublishedBy: published?.metadata.updatedBy,
      deletedAt: published?.deleteMetadata?.deletedAt,
      deletedBy: mapCallerRefToUserId(published?.deleteMetadata?.deletedBy),
      workspaceId: parsedDraft.workspaceId,
      teamId: parsedDraft.teamRef?.id,
      weights: parsedDraft.weights ? [...parsedDraft.weights] : undefined,
      manualCalibration: parsedDraft.manualCalibration
        ? [...parsedDraft.manualCalibration]
        : undefined,
      capping: parsedDraft.capping,
      meetingLimit,
      draftRevision: draft?.metadata.revision,
      publishedRevision: published?.metadata.revision,
    },
  }
}

const distributionDataToUpdatePayload = (
  distribution: DistributionData
): UpdateDistributionRequest => {
  const assignmentTypeConfig: UpdateDistributionRequest['assignmentTypeConfig'] = (() => {
    if (distribution.currentData.assignmentType === 'Meeting') {
      if (distribution.currentData.roundRobinKind === 'Flexible') {
        return {
          type: distribution.currentData.assignmentType,
          limits: distribution.currentData.meetingLimit
            ? {
                type: 'MeetingLimitSet',
                ...distribution.currentData.meetingLimit,
              }
            : { type: 'MeetingLimitUnset' },
          handling: {
            type: 'Flexible',
            reassignmentType: distribution.currentData.handlingConfig?.rescheduleWithAnyTeamMember
              ? 'AnyTeamMember'
              : 'SameUser',
            allowPickingAssignee: Boolean(
              distribution.currentData.handlingConfig?.allowPickingAssignee
            ),
          },
        }
      }
      return {
        type: distribution.currentData.assignmentType,
        limits: distribution.currentData.meetingLimit
          ? {
              type: 'MeetingLimitSet',
              ...distribution.currentData.meetingLimit,
            }
          : { type: 'MeetingLimitUnset' },
        handling: {
          type: 'Strict',
          hideAssignee: Boolean(distribution.currentData.handlingConfig?.hideAssignee),
        },
      }
    }

    return { type: distribution.currentData.assignmentType }
  })()

  return {
    name: distribution.currentData.name,
    capping: distribution.currentData.capping,
    manualCalibration: distribution.currentData.manualCalibration,
    assignmentTypeConfig,
    teamId: distribution.currentData.teamId,
    weights: distribution.currentData.weights,
  }
}

export const getHistoricalDistributions = async ({
  client,
  tenantId,
  workspaceId,
  filters,
}: {
  filters: { since: string; until: string; assignmentType: AssignmentType }
  client: Distribution
  tenantId: string
  workspaceId: string
}) => {
  const distributions = await client.getHistoricalDistributions(tenantId, workspaceId, filters)
  return distributions
    .filter(item => item.assignmentTypeConfig.type === filters.assignmentType)
    .map(item => ({
      id: item.distributionId,
      currentData: {
        name: item.name,
        teamId: item.teamRef.id,
      },
    }))
}

export const getHistoricalDistributionsByIds = async ({
  client,
  tenantId,
  workspaceId,
  ids,
  filters,
}: {
  ids: string[]
  filters: { since: string; until: string }
  client: Distribution
  tenantId: string
  workspaceId: string
}) => {
  if (!ids.length) {
    return {}
  }
  const distributions = await client.getHistoricalDistributions(tenantId, workspaceId, filters)

  const distributionsMap = R.indexBy(distributions, item => item.distributionId)
  const filteredDistributions = ids
    .map(item => distributionsMap[item])
    .map(item => ({
      id: item.distributionId,
      currentData: {
        name: item.name,
        teamId: item.teamRef.id,
        weights: item.weights,
        deletedAt: item.deleteMetadata?.deletedAt,
        deletedBy: mapCallerRefToUserId(item.deleteMetadata?.deletedBy),
        assignmentType: item.assignmentTypeConfig.type,
      },
    }))

  return R.indexBy(filteredDistributions, item => item.id)
}

export const findDistributions = async ({
  client,
  tenantId,
  workspaceId,
  pageSize,
  page,
  query,
}: {
  client: Distribution
  page: number
  pageSize: number
  query?: { name?: string; assignmentType?: AssignmentType }
  tenantId: string
  workspaceId: string
}) => {
  const distributionsData = await client
    .getBulkListOfConfigurations(tenantId, {
      workspaceId: [workspaceId],
      ...query,
    })
    .then(distributions =>
      distributions.map(distribution =>
        getDistributionData({
          draft: distribution.draft
            ? {
                ...distribution.draft,
                distributionId: distribution.distributionId,
                workspaceId,
              }
            : undefined,
          published: distribution.published
            ? {
                ...distribution.published,
                distributionId: distribution.distributionId,
                workspaceId,
              }
            : undefined,
        })
      )
    )

  return {
    list: distributionsData,
    page,
    pageSize,
    total: distributionsData.length,
  }
}

export const featchPublishedDistributions = ({
  client,
  page,
  pageSize,
  tenantId,
  workspaceId,
  search,
  assignmentType,
}: {
  client: Distribution
  page: number
  pageSize: number
  tenantId: string
  workspaceId: string
  search: string
  assignmentType?: 'Record' | 'Meeting' | 'Conversation'
}) =>
  client
    .getBulkListOfPublishedConfigurations(tenantId, {
      workspaceId: [workspaceId],
      page,
      pageSize,
      name: search,
      assignmentType,
    })
    .then(data => ({
      list:
        data.results?.map(item => getDistributionData({ draft: undefined, published: item })) ?? [],
      page: data.page,
      pageSize: data.pageSize,
      total: data.total,
    }))

export const getDistributionDraft = async ({
  client,
  tenantId,
  id,
}: {
  client: Distribution
  id: string
  tenantId: string
}) => {
  const [draft, published] = await Promise.all([
    client.getDraftDistributionConfig(tenantId, id).catch(e => {
      if (e.status === 404) {
        return undefined
      }

      throw e
    }),
    client.getDistributionConfig(tenantId, id).catch(e => {
      if (e.status === 404) {
        return undefined
      }

      throw e
    }),
  ])

  return getDistributionData({ draft, published })
}

export const getDistribution = async ({
  loader,
  tenantId,
  id,
  shouldThrow,
}: {
  id: string
  loader: ReturnType<typeof usePublishedDistributionLoader>
  shouldThrow?: boolean
  tenantId: string
}) => {
  const published = await loader({ tenantId, id }).catch(e => {
    if (e.status === 404 && !shouldThrow) {
      return null
    }

    throw e
  })

  if (!published) {
    return null
  }

  return getDistributionData({ published })
}

export const getDistributionWithExtraData = ({
  loader,
  tenantId,
  shouldThrow,
  id,
}: {
  id: string
  tenantId: string
  shouldThrow?: boolean
  loader: ReturnType<typeof useDistributionWithExtraDataLoader>
}) => {
  return loader({ tenantId, id })
    .then(data =>
      data
        ? {
            id: data.attributes.distributionId,
            currentData: {
              name: data.config.name,
              teamId: data.config.teamRef.id,
              weights: data.config.weights,
              assignmentType: data.config.assignmentTypeConfig.type,
              lastModifiedAt: data.config.metadata.updatedAt,
              lastModifiedBy: data.config.metadata.updatedBy,
              roundRobinKind:
                data.config.assignmentTypeConfig.type === 'Meeting'
                  ? data.config.assignmentTypeConfig.handling.type
                  : 'Strict',
              status: 'published' as const,
              workspaceId: data.config.workspaceId,
              permissions: data.permissions,
            },
          }
        : null
    )
    .catch(e => {
      if (e.status === 404 && !shouldThrow) {
        return null
      }

      throw e
    })
}

export const getGatheredDistribution = ({
  client,
  tenantId,
  id,
}: {
  client: Distribution
  id: string
  tenantId: string
}) => {
  return client.getGatheredInfoOnDistribution(tenantId, id)
}

export const createDistribution = async ({
  client,
  type,
  teamId,
  tenantId,
  workspaceId,
}: {
  client: Distribution
  teamId?: string
  tenantId: string
  type: AssignmentType
  workspaceId: string
}) => {
  return client
    .createDraftDistributionConfig(tenantId, {
      assignmentTypeConfig:
        type === 'Meeting'
          ? {
              type,
              limits: {
                type: 'MeetingLimitUnset',
              },
              handling: {
                type: 'Flexible',
                allowPickingAssignee: false,
                reassignmentType: 'SameUser',
              },
            }
          : { type },
      product: type === 'Meeting' ? 'Concierge' : 'Distro',
      teamId,
      workspaceId,
    })
    .then(result => {
      return getDistributionData({ draft: result })
    })
}

export const updateDistribution = async ({
  client,
  tenantId,
  distribution,
  publishedDistribution,
}: {
  client: Distribution
  distribution: DistributionData
  publishedDistribution?: DistributionData | null
  tenantId: string
}) => {
  const revision = distribution.currentData.draftRevision
  return client
    .updateDraftDistributionConfig(
      tenantId,
      distribution.id,
      distributionDataToUpdatePayload(distribution),
      revision !== undefined
        ? {
            headers: {
              'If-Match': `"${revision}"`,
            },
          }
        : undefined
    )
    .then(result => {
      const parsed = getDistributionData({ draft: result })
      const status = (() => {
        if (!publishedDistribution) {
          return 'draft'
        }

        if (!parsed) {
          return 'published'
        }

        return checkIfHasNewDraftModel({
          draft: parsed,
          published: publishedDistribution,
        })
          ? 'pending-changes'
          : 'published'
      })()

      parsed.currentData.status = status
      return parsed
    })
}

export const deleteDistribution = ({
  client,
  tenantId,
  id,
  draftDistribution,
  publishedDistribution,
}: {
  client: Distribution
  draftDistribution: DistributionData
  id: string
  publishedDistribution?: DistributionData | null
  tenantId: string
}) => {
  const revision = draftDistribution.currentData.draftRevision
  const publishedRevision = publishedDistribution?.currentData.publishedRevision
  const promises: Array<Promise<DraftWrappedConfigurationDTO | WrappedConfigurationDTO>> = []

  if (publishedRevision !== undefined) {
    promises.push(
      client.deleteDistribution(
        tenantId,
        id,
        undefined,
        publishedRevision !== undefined
          ? {
              headers: {
                'If-Match': `"${publishedRevision}"`,
              },
            }
          : undefined
      )
    )
  }

  if (revision !== undefined) {
    promises.push(
      client.deleteDraftRoundRobinDistribution(
        tenantId,
        id,
        revision !== undefined
          ? {
              headers: {
                'If-Match': `"${revision}"`,
              },
            }
          : undefined
      )
    )
  }

  return Promise.all(promises).then(() => {})
}

export const duplicateDistribution = ({
  client,
  workspaceId,
  tenantId,
  id,
}: {
  client: Distribution
  id: string
  tenantId: string
  workspaceId: string
}) => {
  return client
    .getDraftDistributionConfig(tenantId, id)
    .catch(() => client.getDistributionConfig(tenantId, id))
    .then(distribution =>
      client
        .createDraftDistributionConfig(tenantId, {
          workspaceId,
          name: `Copy of ${distribution.name}`,
          assignmentTypeConfig: distribution.assignmentTypeConfig,
          capping: distribution.capping,
          manualCalibration: distribution.manualCalibration,
          teamId: distribution.teamRef?.id,
          weights: distribution.weights,
        })
        .then(distribution => {
          const draft = getDistributionData({
            draft: distribution,
          })

          return { draft }
        })
    )
}

export const publishDistribution = ({
  client,
  tenantId,
  id,
}: {
  client: Distribution
  id: string
  tenantId: string
}) =>
  client
    .publishDistributionConfiguration(tenantId, id)
    .then(result => getDistributionData({ published: result }))

export const getGetOrderedListOfUsers = ({
  client,
  tenantId,
  id,
  query,
}: {
  client: Distribution
  id: string
  tenantId: string
  query?: {
    productFeature?: ProductFeatureWithRoundRobin
    vacationHandling?: UsersOnVacationHandling
  }
}) =>
  client.publicGetOrderedListOfUsers(tenantId, id, query).then(result => {
    return {
      assignmentType: result.assignmentTypeConfig.type,
      roundRobinKind: getRoundRobinKind(result) ?? 'Strict',
      reassignmentType: getReassignmentType(result) ?? 'SameUser',
      users: result.users,
      vacations: result.vacations,
    }
  })

export type OrderedListOfUsers = Awaited<ReturnType<typeof getGetOrderedListOfUsers>>
